import React, { useState } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
  IconButton, Collapse, Button,
} from '@mui/material';
import ReactApexChart from 'react-apexcharts';
import Swal from 'sweetalert2';
import {
  AlertTriangle, ShoppingCart, Factory, PackageMinus, Plus, ChevronRight, ChevronDown,
} from 'lucide-react';
import {
  PageHeader, StatusChip, Panel, StatStrip, FormDialog, LoadGate, PageShell, CHART_FONT,
} from './businessUi';
import {
  computeAllMovements, computeStockLevels, computeMaterialPlan, formatNumber, formatCurrency,
} from './erpData';
import { useErp } from './useErp';
import { ERP_KEYS } from '../../Services/app/erpservice';

const KEYS = {
  inventory: ERP_KEYS.inventory,
  boms: ERP_KEYS.boms,
  production: ERP_KEYS.production,
  manualMoves: ERP_KEYS.movements,
  purchase: ERP_KEYS.purchase,
};

// Open work orders generate demand; everything else is already accounted for.
const OPEN_STATUS = ['Planned', 'In Progress'];
const TODAY = '2026-06-13';

export default function Mrp() {
  const { data, loading, error, save, reload } = useErp(KEYS);
  const [open, setOpen] = useState({});   // expanded pegging rows, keyed by code
  const [poRow, setPoRow] = useState(null); // component a PO is being raised for

  const inventory = data?.inventory || [];
  const boms = data?.boms || [];
  const production = data?.production || [];
  const manualMoves = data?.manualMoves || [];
  const purchase = data?.purchase || [];

  const levels = computeStockLevels(inventory, computeAllMovements(manualMoves, production, boms));
  const openOrders = production.filter((o) => OPEN_STATUS.includes(o.status));

  // Gross requirement per component = Σ (qtyPer × order qty) across open orders.
  // We keep the per-order breakdown (pegging) so each line can be drilled into.
  const reqByCode = {};
  openOrders.forEach((o) => {
    computeMaterialPlan(o, boms, inventory, levels).forEach((p) => {
      if (!reqByCode[p.code]) reqByCode[p.code] = { code: p.code, name: p.name, uom: p.uom, required: 0, pegging: [] };
      reqByCode[p.code].required += p.required;
      reqByCode[p.code].pegging.push({ id: o.id, machine: o.machine, qty: p.required });
    });
  });

  // Net each component against current on-hand stock, and suggest a buy quantity
  // that both covers the shortfall AND restores the reorder buffer.
  const requirements = Object.values(reqByCode).map((r) => {
    const onHand = levels[r.code] ?? 0;
    const item = inventory.find((i) => i.code === r.code);
    const net = Math.max(0, Math.round((r.required - onHand) * 100) / 100);
    const reorder = Number(item?.reorder) || 0;
    const rate = item ? item.rate : 0;
    const suggested = net > 0 ? Math.round((net + reorder) * 100) / 100 : 0;
    return { ...r, onHand, net, reorder, rate, suggested, buyValue: net * rate, short: net > 0 };
  }).sort((a, b) => b.net - a.net);

  const shortItems = requirements.filter((r) => r.short);
  const buyValue = shortItems.reduce((s, r) => s + r.buyValue, 0);

  // Reorder suggestions independent of production demand (below reorder level).
  const reorderItems = inventory
    .filter((i) => (levels[i.code] ?? 0) < (Number(i.reorder) || 0))
    .map((i) => ({ ...i, onHand: levels[i.code] ?? 0 }));

  const toggle = (code) => setOpen((o) => ({ ...o, [code]: !o[code] }));

  // Raising a PO from MRP writes a real Purchase order. The Purchase screen only
  // reads supplier/date/items/amount/status, but we also stash a `lines` array so
  // the component + qty intent is preserved on the record.
  const poFields = (row) => ([
    { name: 'supplier', label: 'Supplier', required: true, helper: `Procuring ${row.name} (${row.code})` },
    { name: 'qty', label: `Order Qty (${row.uom})`, type: 'number', default: row.suggested, helper: `Rate ${formatCurrency(row.rate)} per ${row.uom}` },
    { name: 'date', label: 'PO Date', type: 'date', default: TODAY },
    { name: 'status', label: 'Status', type: 'select', default: 'Open', options: ['Open', 'Partially Received', 'Received'] },
  ]);

  const handleCreatePo = (v) => {
    const row = poRow;
    if (!row) return;
    const qty = Number(v.qty) || 0;
    const amount = Math.round(qty * (row.rate || 0));
    const po = {
      id: `PO-${7700 + purchase.length + 1}`,
      supplier: v.supplier,
      date: v.date,
      items: 1,
      amount,
      status: v.status,
      lines: [{ code: row.code, name: row.name, qty, uom: row.uom, rate: row.rate }],
    };
    save('purchase', [po, ...purchase])
      .then(() => Swal.fire({ icon: 'success', title: 'Purchase order created', text: `${po.id} · ${v.supplier} · ${formatCurrency(amount)}`, timer: 1800, showConfirmButton: false }))
      .catch(() => Swal.fire({ icon: 'error', title: 'Save failed', text: 'Could not save to ThingsBoard. Please retry.', confirmButtonColor: '#5b3df5' }));
  };

  // Top components by net requirement (bar).
  const topShort = shortItems.slice(0, 8);
  const reqOptions = {
    chart: { type: 'bar', toolbar: { show: false }, fontFamily: CHART_FONT },
    plotOptions: { bar: { horizontal: true, borderRadius: 6, borderRadiusApplication: 'end', barHeight: '55%' } },
    colors: ['#f43f5e'],
    dataLabels: { enabled: false },
    legend: { show: false },
    xaxis: { categories: topShort.map((r) => r.name), labels: { style: { fontSize: '11px' } } },
    yaxis: { labels: { style: { fontSize: '12px' } } },
    tooltip: { y: { formatter: (v, opt) => `${formatNumber(v, v % 1 ? 2 : 0)} ${topShort[opt.dataPointIndex]?.uom || ''}` } },
    grid: { borderColor: 'background.default' },
  };

  const empty = production.length === 0 && inventory.length === 0;

  return (
    <PageShell>
      <PageHeader
        title="MRP"
        subtitle="Material Requirements Planning — nets demand from open work orders against stock on hand"
      />

      <FormDialog
        open={poRow !== null}
        title={poRow ? `Raise PO — ${poRow.name}` : 'Raise PO'}
        fields={poRow ? poFields(poRow) : []}
        onClose={() => setPoRow(null)}
        onSubmit={handleCreatePo}
        submitLabel="Create PO"
      />

      <LoadGate loading={loading} error={error} onRetry={reload}>
        {empty ? (
          <Panel title="Nothing to plan yet" accent="linear-gradient(90deg,#7c5cff,#818cf8)">
            <Typography sx={{ p: 4, textAlign: 'center', color: 'text.disabled', fontSize: 14 }}>
              Add inventory items, BOMs and production orders — MRP will then compute what to buy and by how much.
            </Typography>
          </Panel>
        ) : (
          <>
            <Box sx={{ mb: 2.5 }}>
              <StatStrip
                items={[
                  { label: 'Open Work Orders', value: String(openOrders.length), tone: 'sky' },
                  { label: 'Components Required', value: String(requirements.length), tone: 'primary' },
                  { label: 'Items Short', value: String(shortItems.length), tone: 'warning', hint: shortItems.length ? 'need procurement' : 'all covered' },
                  { label: 'Est. Buy Value', value: formatCurrency(buyValue), tone: 'destructive' },
                ]}
              />
            </Box>

            {shortItems.length > 0 && (
              <Panel title="Net Requirement by Component" subtitle="Quantity to procure after netting stock" accent="linear-gradient(90deg,#f43f5e,#fb7185)" sx={{ mb: 2.5 }}>
                <Box sx={{ px: 2, pb: 1 }}>
                  <ReactApexChart options={reqOptions} series={[{ name: 'To Buy', data: topShort.map((r) => r.net) }]} type="bar" height={Math.max(200, topShort.length * 42)} />
                </Box>
              </Panel>
            )}

            <Panel title="Material Requirements Plan" subtitle={`${openOrders.length} open work order(s) exploded through their BOMs · click a row to see what's driving it`} accent="linear-gradient(90deg,#0ea5e9,#38bdf8)" actions={<Factory size={18} color="#0ea5e9" />} sx={{ mb: 2.5 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Component</TableCell>
                      <TableCell align="right">Required</TableCell>
                      <TableCell align="right">On Hand</TableCell>
                      <TableCell align="right">Net to Buy</TableCell>
                      <TableCell align="right">Suggested Buy</TableCell>
                      <TableCell align="right">Est. Cost</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {requirements.length === 0 && (
                      <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', color: 'text.disabled', py: 3 }}>No open work orders generating demand.</TableCell></TableRow>
                    )}
                    {requirements.map((r) => (
                      <React.Fragment key={r.code}>
                        <TableRow hover sx={{ '& > td': { borderBottom: open[r.code] ? 'none' : undefined } }}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <IconButton size="small" onClick={() => toggle(r.code)} sx={{ p: 0.25, color: 'text.secondary' }}>
                                {open[r.code] ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                              </IconButton>
                              <Box>
                                <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.primary' }}>{r.code}</Typography>
                                <Typography component="span" sx={{ fontSize: 13, ml: 1 }}>{r.name}</Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>{formatNumber(r.required, r.required % 1 ? 2 : 0)} {r.uom}</TableCell>
                          <TableCell align="right" sx={{ color: 'text.secondary' }}>{formatNumber(r.onHand, r.onHand % 1 ? 2 : 0)} {r.uom}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: r.short ? '#be123c' : '#047857' }}>
                            {r.short ? `${formatNumber(r.net, r.net % 1 ? 2 : 0)} ${r.uom}` : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ color: r.short ? '#0f172a' : '#94a3b8', fontWeight: r.short ? 600 : 400 }}>
                            {r.short ? `${formatNumber(r.suggested, r.suggested % 1 ? 2 : 0)} ${r.uom}` : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'text.secondary' }}>{r.short ? formatCurrency(r.buyValue) : '—'}</TableCell>
                          <TableCell align="center">
                            {r.short
                              ? <Button size="small" variant="outlined" startIcon={<Plus size={14} />} onClick={() => setPoRow(r)} sx={{ textTransform: 'none', py: 0.1, minWidth: 0 }}>PO</Button>
                              : <StatusChip label="Covered" tone="success" />}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={7} sx={{ py: 0, border: 0 }}>
                            <Collapse in={!!open[r.code]} timeout="auto" unmountOnExit>
                              <Box sx={{ px: 2, py: 1.5, mb: 0.5, bgcolor: 'background.paper', borderRadius: 1 }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', mb: 0.75 }}>
                                  Demand from {r.pegging.length} work order(s)
                                </Typography>
                                {r.pegging.map((pg, i) => (
                                  <Box key={`${pg.id}-${i}`} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, borderBottom: i < r.pegging.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                                    <Typography sx={{ fontSize: 12.5, color: 'text.primary' }}>
                                      <strong>{pg.id}</strong> · <Typography component="span" sx={{ color: 'text.disabled' }}>{pg.machine}</Typography>
                                    </Typography>
                                    <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
                                      {formatNumber(pg.qty, pg.qty % 1 ? 2 : 0)} {r.uom}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Panel>

            <Panel title="Reorder Suggestions" subtitle="Items below their reorder level — replenish independent of work orders" accent="linear-gradient(90deg,#f59e0b,#fbbf24)" actions={<ShoppingCart size={18} color="#f59e0b" />}>
              <Box sx={{ px: 2, pb: 1.5 }}>
                {reorderItems.length === 0 && (
                  <Typography sx={{ fontSize: 13, color: '#047857', py: 2 }}>All items are at or above their reorder level. 🎉</Typography>
                )}
                {reorderItems.map((i, idx) => (
                  <Box key={`${i.code}-${idx}`} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.1, borderBottom: idx < reorderItems.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                    <Box sx={{ width: 34, height: 34, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: (i.onHand <= 0) ? 'rgba(244,63,94,0.12)' : 'rgba(245,158,11,0.14)', color: (i.onHand <= 0) ? '#be123c' : '#b45309', flexShrink: 0 }}>
                      {i.onHand <= 0 ? <PackageMinus size={17} /> : <AlertTriangle size={17} />}
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: 'text.primary' }}>{i.name}</Typography>
                      <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>{i.code} · {i.warehouse || 'Store'}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right', flexShrink: 0, mr: 1 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: (i.onHand <= 0) ? '#be123c' : '#b45309' }}>{formatNumber(i.onHand)} {i.uom}</Typography>
                      <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>reorder {formatNumber(Number(i.reorder) || 0)}</Typography>
                    </Box>
                    <Button
                      size="small" variant="outlined" startIcon={<Plus size={14} />}
                      onClick={() => setPoRow({ code: i.code, name: i.name, uom: i.uom, rate: i.rate || 0, suggested: Math.max(0, Math.round(((Number(i.reorder) || 0) - i.onHand) * 100) / 100) })}
                      sx={{ textTransform: 'none', py: 0.1, minWidth: 0, flexShrink: 0 }}
                    >
                      PO
                    </Button>
                  </Box>
                ))}
              </Box>
            </Panel>
          </>
        )}
      </LoadGate>
    </PageShell>
  );
}
